import { CommandRuntimeView } from './command-execution-runtime';

export const UNASSIGNED_COMMAND_ID = '__unassigned__';

export type ExternalCallTreeSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';
export type RequestTreeKind = 'command' | 'method' | 'http';

export interface ExternalCallTreeLeaf {
  id: string;
  serviceName: string;
  httpMethod: string;
  url: string;
  urlLabel: string;
  statusLabel: string;
  severity: ExternalCallTreeSeverity;
  durationMs: number | null;
  proxyLabel: string | null;
  error: string | null;
  commandExecutionId: string | null;
}

export interface RequestTreeCommandSource {
  id: string;
  title: string;
  state: string;
  severity: ExternalCallTreeSeverity;
  logs: string[];
  inputJson: string;
  resultJson: string;
  attempt?: number | string;
  startedAt?: string | Date;
  completedAt?: string | Date;
  duration?: string;
  workerId?: string;
  commandTypeShort?: string;
  error?: string | null;
  parentCommandId?: string | null;
  runtime?: CommandRuntimeView | null;
}

export interface RequestTreeNode {
  id: string;
  kind: RequestTreeKind;
  title: string;
  isUnassigned: boolean;
  hasCalls: boolean;
  isExternal: boolean;
  expanded: boolean;
  state: string;
  severity: ExternalCallTreeSeverity;
  statusLabel: string | null;
  durationLabel: string | null;
  durationMs: number | null;
  durationPercent: number | null;
  isRunning: boolean;
  proxyLabel: string | null;
  callId: string | null;
  url: string | null;
  httpMethod: string | null;
  logs: string[];
  inputJson: string;
  resultJson: string;
  attempt: number | string;
  startedAt?: string | Date;
  duration: string;
  workerId: string;
  commandTypeShort: string;
  error: string | null;
  runtime: CommandRuntimeView | null;
  rssDeltaLabel: string | null;
  children: RequestTreeNode[];
}

export function shortExternalCallUrl(url: string): string {
  if (!url || url === '—') {
    return url || '—';
  }
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    return stripUrlQuery(url);
  }
}

export function externalCallUrlWithoutQuery(url: string): string {
  if (!url || url === '—') {
    return url || '—';
  }
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return stripUrlQuery(url);
  }
}

export function externalCallRequestPath(url: string): string {
  if (!url || url === '—') {
    return url || '—';
  }
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
  } catch {
    return url;
  }
}

function stripUrlQuery(url: string): string {
  const queryStart = url.indexOf('?');
  const hashStart = url.indexOf('#');
  let end = url.length;
  if (queryStart >= 0) {
    end = Math.min(end, queryStart);
  }
  if (hashStart >= 0) {
    end = Math.min(end, hashStart);
  }
  return url.slice(0, end);
}

export function formatHttpRequestLine(
  method?: string | null,
  url?: string | null,
): string | null {
  const verb = (method ?? '').trim();
  const target = (url ?? '').trim();
  if (!target || target === '—') {
    return verb || null;
  }
  return verb ? `${verb} ${target}` : target;
}

export function formatHttpQueryParametersJson(url?: string | null): string | null {
  const target = (url ?? '').trim();
  if (!target || target === '—') {
    return null;
  }
  try {
    const parsed = target.includes('://')
      ? new URL(target)
      : new URL(target, 'https://placeholder.local');
    if (![...parsed.searchParams.keys()].length) {
      return null;
    }
    const parameters: Record<string, string | string[]> = {};
    for (const key of parsed.searchParams.keys()) {
      const values = parsed.searchParams.getAll(key);
      parameters[key] = values.length > 1 ? values : (values[0] ?? '');
    }
    return JSON.stringify(parameters, null, 2);
  } catch {
    return null;
  }
}

export function hasCapturedHttpBody(value?: string | null): boolean {
  const text = (value ?? '').trim();
  return !!text && text !== '[no body]' && !text.startsWith('[not stored:');
}

export function toggleTreeId(ids: Set<string>, id: string): void {
  if (ids.has(id)) {
    ids.delete(id);
    return;
  }
  ids.add(id);
}

export function findRequestTreeNode(nodes: RequestTreeNode[], id: string): RequestTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    const child = findRequestTreeNode(node.children, id);
    if (child) {
      return child;
    }
  }
  return null;
}

export function requestTreeHasId(nodes: RequestTreeNode[], id: string): boolean {
  return findRequestTreeNode(nodes, id) !== null;
}

export function applyRequestTreeExpand(
  nodes: RequestTreeNode[],
  expandedIds: ReadonlySet<string>,
): RequestTreeNode[] {
  return nodes.map(node => ({
    ...node,
    expanded: node.kind === 'http' ? false : expandedIds.has(node.id),
    children: applyRequestTreeExpand(node.children, expandedIds),
  }));
}

/** Distinct step keys whose latest attempt already succeeded. */
export function countCompletedRequestSteps(
  commands: Array<{
    stepKey: string;
    state: string;
    attempt?: number | string;
    startedAt?: string | Date;
  }>,
): number {
  const latest = new Map<string, { state: string; attempt: number; startedAt: number }>();
  for (const command of commands) {
    const key = command.stepKey || '—';
    const attempt = typeof command.attempt === 'number' && Number.isFinite(command.attempt)
      ? command.attempt
      : Number(command.attempt);
    const attemptNumber = Number.isFinite(attempt) ? attempt : 0;
    const startedAt = command.startedAt ? new Date(command.startedAt).getTime() : 0;
    const current = latest.get(key);
    if (
      !current
      || attemptNumber > current.attempt
      || (attemptNumber === current.attempt && startedAt >= current.startedAt)
    ) {
      latest.set(key, {
        state: command.state,
        attempt: attemptNumber,
        startedAt: Number.isFinite(startedAt) ? startedAt : 0,
      });
    }
  }
  let completed = 0;
  for (const row of latest.values()) {
    if (row.state === 'Succeeded') {
      completed += 1;
    }
  }
  return completed;
}

export function collectExpandableIds(nodes: RequestTreeNode[], into = new Set<string>()): Set<string> {
  for (const node of nodes) {
    if (node.kind === 'command' || node.kind === 'method') {
      into.add(node.id);
    }
    collectExpandableIds(node.children, into);
  }
  return into;
}

export function buildRequestStepTree(
  commands: RequestTreeCommandSource[],
  calls: ExternalCallTreeLeaf[],
  expandedIds: ReadonlySet<string> | null,
  now = Date.now(),
): RequestTreeNode[] {
  const callsByCommand = new Map<string, ExternalCallTreeLeaf[]>();
  const orphans: ExternalCallTreeLeaf[] = [];
  const commandIds = new Set(commands.map(command => command.id).filter(id => !!id));

  for (const call of calls) {
    const commandId = call.commandExecutionId;
    if (!commandId || !commandIds.has(commandId)) {
      orphans.push(call);
      continue;
    }
    const bucket = callsByCommand.get(commandId) ?? [];
    bucket.push(call);
    callsByCommand.set(commandId, bucket);
  }

  const nodeById = new Map<string, RequestTreeNode>();
  const commandById = new Map(commands.map(command => [command.id, command]));
  for (const command of commands) {
    nodeById.set(
      command.id,
      toCommandNode(command, callsByCommand.get(command.id) ?? [], false, expandedIds, now),
    );
  }

  const roots: RequestTreeNode[] = [];
  for (const command of commands) {
    const node = nodeById.get(command.id);
    if (!node) {
      continue;
    }
    const parentId = command.parentCommandId;
    const parent = parentId ? nodeById.get(parentId) : undefined;
    if (parent && parent.id !== node.id && !wouldCreateCommandCycle(commandById, parentId, command.id)) {
      insertChildCommand(parent, node);
      continue;
    }
    roots.push(node);
  }

  if (orphans.length) {
    roots.push(toCommandNode(
      {
        id: UNASSIGNED_COMMAND_ID,
        title: 'Unassigned command',
        state: '—',
        severity: 'secondary',
        logs: [],
        inputJson: '—',
        resultJson: '—',
        attempt: '—',
        duration: '—',
        workerId: '—',
        commandTypeShort: '',
        error: null,
      },
      orphans,
      true,
      expandedIds,
      now,
    ));
  }

  applyDurationScale(roots);
  return roots;
}

/** Formats measured step time without producing sixty-second or sixty-minute remainders. */
export function formatRequestStepDuration(durationMs: number | null): string {
  if (durationMs === null || !Number.isFinite(durationMs) || durationMs < 0) {
    return '—';
  }
  const milliseconds = Math.round(durationMs);
  if (milliseconds < 1000) {
    return `${milliseconds} ms`;
  }
  const seconds = Math.round(milliseconds / 1000);
  if (seconds < 60) {
    return `${seconds} s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min ${seconds % 60} s`;
  }
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

function commandDurationMs(command: RequestTreeCommandSource, now: number): number | null {
  if (!command.startedAt) {
    return null;
  }
  const start = new Date(command.startedAt).getTime();
  const end = command.completedAt != null
    ? new Date(command.completedAt).getTime()
    : command.state === 'Running' ? now : NaN;
  const duration = end - start;
  return Number.isFinite(start) && Number.isFinite(end) && duration >= 0 ? duration : null;
}

function applyDurationScale(roots: RequestTreeNode[]): void {
  const measured: RequestTreeNode[] = [];
  let longestDuration = 0;
  const visit = (nodes: RequestTreeNode[]): void => {
    for (const node of nodes) {
      if (node.kind !== 'method') {
        node.durationLabel = formatRequestStepDuration(node.durationMs);
        if (node.kind === 'command') {
          node.duration = node.durationLabel;
        }
        if (node.durationMs !== null) {
          measured.push(node);
          longestDuration = Math.max(longestDuration, node.durationMs);
        }
      }
      visit(node.children);
    }
  };
  visit(roots);
  for (const node of measured) {
    node.durationPercent = longestDuration > 0 ? (node.durationMs! / longestDuration) * 100 : 0;
  }
}

function wouldCreateCommandCycle(
  commandsById: ReadonlyMap<string, RequestTreeCommandSource>,
  parentId: string | null | undefined,
  childId: string,
): boolean {
  let current = parentId ?? undefined;
  const seen = new Set<string>();
  while (current) {
    if (current === childId || seen.has(current)) {
      return true;
    }
    seen.add(current);
    current = commandsById.get(current)?.parentCommandId ?? undefined;
  }
  return false;
}

function insertChildCommand(parent: RequestTreeNode, child: RequestTreeNode): void {
  const methodStart = parent.children.findIndex(node => node.kind !== 'command');
  if (methodStart === -1) {
    parent.children.push(child);
  } else {
    parent.children.splice(methodStart, 0, child);
  }
  if (child.hasCalls || child.children.length > 0) {
    parent.hasCalls = true;
  }
}

function emptyCommandMeta(): Pick<
  RequestTreeNode,
  'attempt' | 'startedAt' | 'duration' | 'workerId' | 'commandTypeShort' | 'error' | 'runtime' | 'rssDeltaLabel'
> {
  return {
    attempt: '—',
    startedAt: undefined,
    duration: '—',
    workerId: '—',
    commandTypeShort: '',
    error: null,
    runtime: null,
    rssDeltaLabel: null,
  };
}

function toCommandNode(
  command: RequestTreeCommandSource,
  calls: ExternalCallTreeLeaf[],
  isUnassigned: boolean,
  expandedIds: ReadonlySet<string> | null,
  now: number,
): RequestTreeNode {
  const children: RequestTreeNode[] = [];
  const callsByService = new Map<string, ExternalCallTreeLeaf[]>();

  for (const call of calls) {
    const bucket = callsByService.get(call.serviceName) ?? [];
    bucket.push(call);
    callsByService.set(call.serviceName, bucket);
  }

  for (const [serviceName, serviceCalls] of callsByService) {
    const methodId = `${command.id}:method:${serviceName}`;
    children.push({
      id: methodId,
      kind: 'method',
      title: serviceName,
      isUnassigned: false,
      hasCalls: true,
      isExternal: false,
      expanded: expandedIds ? expandedIds.has(methodId) : true,
      state: '—',
      severity: 'info',
      statusLabel: null,
      durationLabel: null,
      durationMs: null,
      durationPercent: null,
      isRunning: false,
      proxyLabel: null,
      callId: null,
      url: null,
      httpMethod: null,
      logs: [],
      inputJson: '—',
      resultJson: '—',
      ...emptyCommandMeta(),
      children: serviceCalls.map(call => toHttpNode(call)),
    });
  }

  return {
    id: command.id,
    kind: 'command',
    title: command.title,
    isUnassigned,
    hasCalls: calls.length > 0,
    isExternal: false,
    expanded: expandedIds ? expandedIds.has(command.id) : true,
    state: command.state,
    severity: command.severity,
    statusLabel: null,
    durationLabel: null,
    durationMs: commandDurationMs(command, now),
    durationPercent: null,
    isRunning: command.state === 'Running' && command.completedAt == null,
    proxyLabel: null,
    callId: null,
    url: null,
    httpMethod: null,
    logs: command.logs,
    inputJson: command.inputJson,
    resultJson: command.resultJson,
    attempt: command.attempt ?? '—',
    startedAt: command.startedAt,
    duration: command.duration ?? '—',
    workerId: command.workerId ?? '—',
    commandTypeShort: command.commandTypeShort ?? '',
    error: command.error ?? null,
    runtime: command.runtime ?? null,
    rssDeltaLabel: command.runtime?.rssDeltaLabel ?? null,
    children,
  };
}

function toHttpNode(call: ExternalCallTreeLeaf): RequestTreeNode {
  return {
    id: `http:${call.id}`,
    kind: 'http',
    title: call.urlLabel,
    isUnassigned: false,
    hasCalls: true,
    isExternal: true,
    expanded: false,
    state: '—',
    severity: call.severity,
    statusLabel: call.statusLabel,
    durationLabel: null,
    durationMs: call.durationMs !== null && Number.isFinite(call.durationMs) && call.durationMs >= 0
      ? call.durationMs : null,
    durationPercent: null,
    isRunning: false,
    proxyLabel: call.proxyLabel,
    callId: call.id,
    url: call.url,
    httpMethod: call.httpMethod,
    logs: [],
    inputJson: '—',
    resultJson: '—',
    ...emptyCommandMeta(),
    error: call.error,
    children: [],
  };
}
